/**
 * Firebase Cloud Messaging — frontend bootstrap.
 *
 * The service is intentionally lazy: nothing happens until one of the two
 * entry points is called. They differ on exactly one thing, and it is the most
 * consequential decision in this file:
 *
 *  - {@link registerForUser} NEVER prompts. It registers a token when the
 *    permission is already `granted` and reports `permission-default`
 *    otherwise. Safe to call on every app load, from any shell.
 *  - {@link requestPermissionAndRegister} prompts. Call it ONLY from a user
 *    gesture — a switch, a banner button.
 *
 * Why the split is not cosmetic: `Notification.requestPermission()` can be
 * answered "block", and a blocked origin cannot be un-blocked by the page. The
 * app gets exactly one chance per user, forever. Firing it from a shell's
 * `ngOnInit` spends that chance on someone who was walking past — which is what
 * this file used to do, harmlessly only because the empty VAPID key made it
 * return before reaching the prompt. The moment a key was configured, that
 * became a prompt on first load for every tenant.
 *
 * Tokens are written under `users/{uid}/fcmTokens/{tokenId}` so the backend
 * `sendPushToUser` helper can multicast across all of a user's devices and
 * prune the dead ones.
 */

import { Injectable, inject } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { Firestore, doc, serverTimestamp, setDoc } from '@angular/fire/firestore';

import { LoggerService } from '@core/services/infra/logger.service';
import { environment } from 'environments/environment';

interface MessagingLike {
  // Subset of the firebase/messaging Messaging type we actually use.
  // Importing the live type would force `firebase/messaging` to load
  // eagerly across the bundle — we keep the surface minimal instead.
  readonly app: FirebaseApp;
}

interface FirebaseMessagingApi {
  isSupported: () => Promise<boolean>;
  getMessaging: (app: FirebaseApp) => MessagingLike;
  getToken: (
    messaging: MessagingLike,
    options: { vapidKey: string; serviceWorkerRegistration?: ServiceWorkerRegistration },
  ) => Promise<string>;
  onMessage: (messaging: MessagingLike, cb: (payload: unknown) => void) => () => void;
  deleteToken: (messaging: MessagingLike) => Promise<boolean>;
}

/**
 * Why no token came back.
 *
 * `permission-default` and `permission-denied` are kept apart on purpose:
 * the first is a question nobody has asked yet (a banner may still ask), the
 * second is a door the page can no longer open (only the browser's own site
 * settings can). Collapsing them would make the UI offer a button that cannot
 * work.
 */
export type FcmBlockedReason =
  | 'no-vapid-key'
  | 'permission-default'
  | 'permission-denied'
  | 'unsupported'
  | 'error';

export interface FcmRegistrationResult {
  readonly supported: boolean;
  readonly token?: string;
  readonly reason?: FcmBlockedReason;
}

/** What the browser currently allows, plus the case where it has no opinion. */
export type FcmPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

@Injectable({ providedIn: 'root' })
export class FcmService {
  private readonly app = inject(FirebaseApp);
  private readonly firestore = inject(Firestore);
  private readonly logger = inject(LoggerService);

  private apiPromise: Promise<FirebaseMessagingApi | null> | null = null;
  private currentTokenId: string | null = null;

  /**
   * Whether web-push is configured on this environment at all.
   *
   * Distinct from "the browser refused": an empty VAPID key is OUR gap, and UI
   * that blames the browser for it sends the user hunting through settings for
   * something only a deploy can fix.
   */
  isConfigured(): boolean {
    return !!(environment.firebase?.vapidKey ?? '');
  }

  /** What the browser currently allows. */
  permissionState(): FcmPermissionState {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission as FcmPermissionState;
  }

  /**
   * Register a push token — WITHOUT ever prompting.
   *
   * Safe to call on every app load. When the permission has not been answered
   * yet it returns `permission-default` and leaves the question to a deliberate
   * opt-in, so the app's single irreversible prompt is never spent by a page
   * that merely loaded.
   *
   * @param userId The signed-in user.
   * @return The token, or the reason there is none.
   */
  async registerForUser(userId: string): Promise<FcmRegistrationResult> {
    return this.register(userId, false);
  }

  /**
   * Ask for the notification permission, then register.
   *
   * ONLY from a user gesture. `Notification.requestPermission()` may be
   * answered "block", which the page can never undo — so it belongs behind an
   * explicit act, where the user knows what they are being asked.
   *
   * @param userId The signed-in user.
   * @return The token, or the reason there is none.
   */
  async requestPermissionAndRegister(userId: string): Promise<FcmRegistrationResult> {
    return this.register(userId, true);
  }

  private async register(userId: string, allowPrompt: boolean): Promise<FcmRegistrationResult> {
    if (!userId) return { supported: false, reason: 'error' };

    const vapidKey = environment.firebase?.vapidKey ?? '';
    if (!vapidKey) {
      this.logger.debug('[FcmService] VAPID key not configured — skipping registration');
      return { supported: false, reason: 'no-vapid-key' };
    }

    if (typeof Notification === 'undefined' || typeof navigator === 'undefined') {
      return { supported: false, reason: 'unsupported' };
    }

    const api = await this.loadApi();
    if (!api) return { supported: false, reason: 'unsupported' };

    if (Notification.permission === 'denied') {
      return { supported: true, reason: 'permission-denied' };
    }
    if (Notification.permission === 'default') {
      if (!allowPrompt) return { supported: true, reason: 'permission-default' };
      const result = await Notification.requestPermission();
      if (result !== 'granted') {
        // "dismissed" also lands here. Reporting it as `denied` is the honest
        // read for the caller: no token now, and we must not re-prompt on our
        // own — but the browser will still accept a later, user-initiated ask.
        return {
          supported: true,
          // `result` IS the answer; re-reading `Notification.permission` here
          // also kept TS narrowed to 'default' and made the test unreachable.
          reason: result === 'denied' ? 'permission-denied' : 'permission-default',
        };
      }
    }

    try {
      const messaging = api.getMessaging(this.app);
      const swRegistration = await this.ensureServiceWorker();
      const token = await api.getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });
      if (!token) {
        return { supported: true, reason: 'permission-denied' };
      }
      await this.persistToken(userId, token);
      return { supported: true, token };
    } catch (err) {
      this.logger.warn('[FcmService] getToken failed', 'FcmService', err);
      return { supported: true, reason: 'error' };
    }
  }

  /**
   * Subscribe to foreground push messages. Returns an unsubscribe function
   * (no-op when FCM is not supported / not configured).
   */
  async onForegroundMessage(callback: (payload: unknown) => void): Promise<() => void> {
    const api = await this.loadApi();
    if (!api) return () => undefined;
    try {
      const messaging = api.getMessaging(this.app);
      return api.onMessage(messaging, callback);
    } catch (err) {
      this.logger.warn('[FcmService] onMessage failed', 'FcmService', err);
      return () => undefined;
    }
  }

  private async loadApi(): Promise<FirebaseMessagingApi | null> {
    if (!this.apiPromise) {
      this.apiPromise = (async () => {
        try {
          // Dynamic import keeps `firebase/messaging` out of the main bundle
          // for users who never visit the tenant shell.
          const mod = await import('firebase/messaging');
          const api: FirebaseMessagingApi = {
            isSupported: mod.isSupported as () => Promise<boolean>,
            getMessaging: mod.getMessaging as FirebaseMessagingApi['getMessaging'],
            getToken: mod.getToken as FirebaseMessagingApi['getToken'],
            onMessage: mod.onMessage as FirebaseMessagingApi['onMessage'],
            deleteToken: mod.deleteToken as FirebaseMessagingApi['deleteToken'],
          };
          if (!(await api.isSupported())) return null;
          return api;
        } catch (err) {
          this.logger.warn('[FcmService] firebase/messaging unavailable', 'FcmService', err);
          return null;
        }
      })();
    }
    return this.apiPromise;
  }

  private async ensureServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
    if (!('serviceWorker' in navigator)) return undefined;
    try {
      return await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
      });
    } catch (err) {
      this.logger.warn(
        '[FcmService] could not register messaging SW',
        'FcmService',
        err,
      );
      return undefined;
    }
  }

  private async persistToken(userId: string, token: string): Promise<void> {
    const tokenId = this.tokenDocId(token);
    this.currentTokenId = tokenId;
    const ref = doc(this.firestore, `users/${userId}/fcmTokens/${tokenId}`);
    await setDoc(
      ref,
      {
        token,
        platform: 'web',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        lastUsedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  private tokenDocId(token: string): string {
    // Hash via SubtleCrypto when available; fall back to a stable slice so
    // the doc id stays under Firestore's 1500-byte limit. Two devices with
    // the same token always end up at the same doc id (idempotent).
    return token.length > 100 ? token.slice(-100) : token;
  }
}
